import { ui } from "@patient-watch/ui";
import { AlertTriangle } from "lucide-react";
import { useDiaryByIdQuery } from "@/features/diaries/api";
import Call from "@/components/common/Call";

// Email domains with reliable deliverability
const RELIABLE_EMAIL_DOMAINS = ["gmail", "outlook", "icloud"];

const hasReliableEmailDomain = (email: string | null): boolean => {
    if (!email || !email.includes("@")) return true; // Don't warn if no email
    const domain = email.split("@")[1]?.toLowerCase() ?? "";
    return RELIABLE_EMAIL_DOMAINS.some((reliable) => domain.includes(reliable));
};
import DiaryTitle from "@/features/patients/features/interface/components/DiaryTitle";
import InterfaceButtons from "@/features/patients/features/interface/components/InterfaceButtons";
import { usePatientsCopy } from "@/features/i18n/patientsCopy";
import { useProfileByIdQuery } from "@/features/profile/api/selectProfile";
import {
    AdminEmailDialog,
    AdminPhoneDialog,
    DateOfBirthDialog,
    EmailDialog,
    NameDialog,
    PhoneDialog,
    UserIdDialog,
} from "@/features/profile/components/EditProfileDialogs";
import { useAuth } from "@/utils/auth";
import { isFieldCollected } from "@/features/diaries/utils/collectionPolicy";
import { isDisplayableHospitalNumber } from "@/features/diaries/utils/missingDetailsBuilders";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense } from "react";
import EditDialog from "../../../../components/common/EditDialog";
import SharedAttributeHospitalNumberForm from "../../../../features/shared_attributes/components/SharedAttributeHospitalNumberForm";
import type { SharedAttributeJSON } from "../../../../features/shared_attributes/types";

const EditDiaryPatientsPage = () => {
    const copy = usePatientsCopy();
    const auth = useAuth();
    const { diaryId } = Route.useParams();
    const { data: diary } = useDiaryByIdQuery(diaryId);
    const { data: profile } = useProfileByIdQuery(diary?.patient_id ?? "");

    if (!diary || !profile) {
        return null;
    }

    const isAdmin = auth?.roles?.isAdmin || auth?.isSuperUser;
    const canEditPatientDetails = auth?.roles?.isClinician || isAdmin;
    const showHospitalNumber =
        isFieldCollected(diary, "hospital_number") ||
        isDisplayableHospitalNumber(diary.shared_attributes?.hospital_number);

    return (
        <section className="flex flex-col gap-y-2">
            <InterfaceButtons diaryId={diaryId} className="mt-0 pb-0" />
            <div className="flex flex-col gap-2 px-1 pb-8">
                <Suspense fallback={<ui.Skeleton className="w-48 h-10" />}>
                    <DiaryTitle
                        patientName={profile.name ?? ""}
                        title={profile.title}
                        firstName={profile.first_name}
                        lastName={profile.last_name}
                        email={profile.email}
                        isEditing={true}
                        diaryId={diaryId}
                    />
                </Suspense>
                {/* <PatientCard profile={profile} editable={true} /> */}
                <Suspense fallback={<ui.Skeleton className="w-full h-60" />}>
                    <ui.Card className="">
                        <ui.CardHeader className="px-4 py-3">
                            <ui.CardTitle className="text-lg">{copy.patientDetails}</ui.CardTitle>
                        </ui.CardHeader>
                        <ui.CardContent className="">
                            {isAdmin && <UserIdDialog editable={false} profile={profile} />}
                            <NameDialog editable={true} profile={profile} />
                            <DateOfBirthDialog editable={true} profile={profile} />
                            {canEditPatientDetails ? (
                                <AdminEmailDialog editable={true} profile={profile} locked={false} />
                            ) : (
                                <EmailDialog editable={true} profile={profile} locked={true} />
                            )}
                            {!hasReliableEmailDomain(profile.email) && (
                                <Call
                                    icon={<AlertTriangle />}
                                    words={copy.reliableEmailWarning}
                                    variant="warning"
                                    size="xs"
                                    className="mb-4"
                                ></Call>
                            )}
                            {canEditPatientDetails ? (
                                <AdminPhoneDialog editable={true} profile={profile} locked={false} />
                            ) : (
                                <PhoneDialog editable={true} profile={profile} locked={true} />
                            )}
                            {showHospitalNumber && (
                                <EditDialog label="Hospital Number" info={diary.shared_attributes?.hospital_number}>
                                    <SharedAttributeHospitalNumberForm
                                        sharedAttributes={diary.shared_attributes as SharedAttributeJSON | undefined}
                                        diaryId={diaryId}
                                        hideUpdateLinkedItemsOption={auth.roles?.isPatientOnly ?? false}
                                    />
                                </EditDialog>
                            )}
                            {/* <NhsNumberDialog profile={profile} /> */}
                        </ui.CardContent>
                    </ui.Card>
                </Suspense>
            </div>
        </section>
    );
};

export const Route = createFileRoute("/_app/patients/$diaryId/edit")({
    component: EditDiaryPatientsPage,
});
